import { Copy, Eye, EyeOff, RefreshCcw, Save, Send } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '../../components/ConfirmModal';
import SortableTable from '../../components/SortableTable';
import useAdminAction from '../../hooks/useAdminAction';
import useAdminRole from '../../hooks/useAdminRole';
import { ADMIN_ACTIONS } from '../../constants/adminActions';
import {
  WEBHOOK_EVENTS,
  fetchPlatformIntegrations,
  fetchWebhookDeliveryLog,
  platformIntegrationsRef,
  sendPlatformWebhookTest,
} from '../../services/integrations';

function maskSecret(secret) {
  if (!secret) return 'Not generated';
  return `${'*'.repeat(14)}${secret.slice(-4)}`;
}

function makeSecret() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function formatTime(value) {
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function Webhooks() {
  const { isSuperAdmin } = useAdminRole();
  const canEdit = isSuperAdmin;
  const { performAction, loading: actionLoading } = useAdminAction();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [confirmSecret, setConfirmSecret] = useState(false);
  const [tableFilter, setTableFilter] = useState('');

  const load = async () => {
    setLoading(true);
    const [latestConfig, latestDeliveries] = await Promise.all([
      fetchPlatformIntegrations(),
      fetchWebhookDeliveryLog(20),
    ]);
    setConfig(latestConfig);
    setForm(latestConfig.webhook);
    setDeliveries(latestDeliveries);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = useMemo(() => JSON.stringify(config?.webhook || {}) !== JSON.stringify(form || {}), [config, form]);

  const toggleEvent = (eventName) => {
    setForm((current) => {
      const events = current.events || [];
      const nextEvents = events.includes(eventName)
        ? events.filter((item) => item !== eventName)
        : [...events, eventName];
      return { ...current, events: nextEvents };
    });
  };

  const saveWebhook = async (nextWebhook = form, action = ADMIN_ACTIONS.PLATFORM_WEBHOOK_UPDATED) => {
    if (!canEdit || !nextWebhook) return;
    await performAction({
      action,
      before: config?.webhook || null,
      after: nextWebhook,
      writeFn: (batch, batchDb) => {
        batch.set(platformIntegrationsRef(batchDb), {
          webhook: {
            ...nextWebhook,
            updatedAt: Timestamp.now(),
          },
          updatedAt: Timestamp.now(),
        }, { merge: true });
      },
    });
    await load();
  };

  const regenerateSecret = async () => {
    const nextWebhook = { ...form, secret: makeSecret() };
    setConfirmSecret(false);
    setForm(nextWebhook);
    await saveWebhook(nextWebhook, ADMIN_ACTIONS.PLATFORM_WEBHOOK_SECRET_REGENERATED);
  };

  const sendTestEvent = async () => {
    if (!canEdit || !form) return;
    await sendPlatformWebhookTest('bill.created');
    await load();
  };

  if (loading || !form) {
    return (
      <div className="wb-stack">
        <div className="wb-skeleton wb-skeleton--large" />
        <div className="wb-skeleton wb-skeleton--table" />
      </div>
    );
  }

  return (
    <div className="wb-stack">
      {!canEdit ? (
        <div className="wb-warning-banner">Read-only: only super_admin users can edit platform webhooks.</div>
      ) : null}

      <section className="wb-card wb-card--elevated">
        <div className="wb-card-head">
          <div>
            <h3 className="wb-section-title">Webhook Configuration</h3>
            <p className="wb-muted">Central platform webhook used by the WhatsApp billing bot.</p>
          </div>
          <span className={`wb-status-pill ${form.enabled ? 'is-online' : 'is-offline'}`}>
            {form.enabled ? 'Active' : 'Disabled'}
          </span>
        </div>

        <label className="wb-field">
          <span>Endpoint URL</span>
          <input
            value={form.endpointUrl || ''}
            disabled={!canEdit}
            onChange={(event) => setForm((current) => ({ ...current, endpointUrl: event.target.value }))}
          />
        </label>

        <label className="wb-field">
          <span>Webhook Secret</span>
          <div className="wb-secret-row">
            <input
              type={showSecret ? 'text' : 'password'}
              value={showSecret ? form.secret || '' : maskSecret(form.secret)}
              readOnly
            />
            <button type="button" className="secondary" onClick={() => setShowSecret((value) => !value)}>
              {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              {showSecret ? 'Hide' : 'Show'}
            </button>
            <button type="button" className="danger" disabled={!canEdit || actionLoading} onClick={() => setConfirmSecret(true)}>
              <RefreshCcw size={16} />
              Regenerate
            </button>
          </div>
        </label>

        <label className="wb-checkbox-row">
          <input
            type="checkbox"
            checked={Boolean(form.enabled)}
            disabled={!canEdit}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
          />
          <span>Enable webhook delivery</span>
        </label>
      </section>

      <section className="wb-card">
        <h3 className="wb-section-title">Subscribed Events</h3>
        <div className="wb-check-grid">
          {WEBHOOK_EVENTS.map((eventName) => (
            <label key={eventName} className="wb-check-tile">
              <input
                type="checkbox"
                checked={(form.events || []).includes(eventName)}
                disabled={!canEdit}
                onChange={() => toggleEvent(eventName)}
              />
              <span>{eventName}</span>
            </label>
          ))}
        </div>
        <div className="wb-actions-line">
          <button type="button" disabled={!canEdit || !dirty || actionLoading} onClick={() => saveWebhook()}>
            <Save size={16} />
            Save Changes
          </button>
          <button type="button" className="secondary" disabled={!canEdit || actionLoading} onClick={sendTestEvent}>
            <Send size={16} />
            Send Test Event
          </button>
        </div>
      </section>

      <section className="wb-card">
        <div className="wb-card-head">
          <h3 className="wb-section-title">Delivery Log</h3>
          <input
            className="wb-filter-input"
            placeholder="Filter deliveries"
            value={tableFilter}
            onChange={(event) => setTableFilter(event.target.value)}
          />
        </div>
        <SortableTable
          rows={deliveries}
          filter={tableFilter}
          columns={[
            { key: 'time', header: 'Time', accessor: 'timestamp', render: (row) => formatTime(row.timestamp) },
            { key: 'event', header: 'Event', accessor: 'event' },
            { key: 'status', header: 'Status', accessor: 'status', render: (row) => `${row.status || '-'} ${Number(row.status) < 400 ? 'OK' : 'FAIL'}` },
            { key: 'ms', header: 'ms', accessor: 'durationMs', render: (row) => `${row.durationMs || 0}ms` },
          ]}
        />
      </section>

      <ConfirmModal
        open={confirmSecret}
        title="Regenerate webhook secret?"
        description="Regenerating the webhook secret will disconnect the WhatsApp billing bot until the new secret is updated in the bot server. Are you sure?"
        risk="high"
        onCancel={() => setConfirmSecret(false)}
        onConfirm={regenerateSecret}
      />
    </div>
  );
}
